import { sendToUnityWithResponse } from "../../components/Unityboards/UnityBridge";
import { useViewRDataStore } from "../../store/viewRDataStore";
import { ViewRData, VSEData } from "../../types/unity";
import {
  reloadUpdatedViewrBeforeEditorImport,
  resolveEditorImportVseIndex,
} from "./editorImportViewrReload";

const DEFAULT_VIEWR_DATA_SYNC_TIMEOUT_MS = 15000;

export interface EditorImportVseReadiness {
  viewRData: ViewRData;
  targetSpaceIndex: number;
  vseIndex: number;
  vseGcsUrl: string;
  vsePublicUrl: string;
  fileName: string;
}

export interface PreparedEditorImportVseReadiness
  extends EditorImportVseReadiness {
  transactionId: string;
}

export interface EditorImportMeshFallback {
  vseGCSUrl?: string | null;
  vsePublicUrl?: string | null;
  fileName?: string | null;
}

function normalizeGsUrl(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function readVseFileUrls(vse: VSEData | null | undefined): {
  vseGcsUrl: string;
  vsePublicUrl: string;
  fileName: string;
} {
  const fileInfo = vse?.vseFileInfo;
  const platform = vse?.vsePlatformFiles?.[0];
  const vseGcsUrl =
    normalizeGsUrl(fileInfo?.vseGCSUrl) || normalizeGsUrl(platform?.vseGCSUrl);
  const vsePublicUrl =
    normalizeGsUrl(fileInfo?.vsePublicUrl) ||
    normalizeGsUrl(platform?.vsePublicUrl);
  const fileName =
    (fileInfo?.fileName ?? platform?.fileName ?? vse?.name ?? "asset.vse").trim() ||
    "asset.vse";
  return { vseGcsUrl, vsePublicUrl, fileName };
}

/** Converter가 index에 반영한 VSE 슬롯·vseFileInfo가 있는지 검사합니다. */
export function validateEditorImportVseEntry(
  viewRData: ViewRData | null | undefined,
  targetSpaceIndex: number,
  vseIndex: number,
): EditorImportVseReadiness {
  if (!viewRData?.spaces?.length) {
    throw new Error("editor_import_viewr_data_missing");
  }

  const space = viewRData.spaces[targetSpaceIndex];
  if (!space) {
    throw new Error("editor_import_space_missing");
  }

  const vse = space.vseList?.[vseIndex];
  if (!vse) {
    throw new Error("editor_import_vse_entry_missing");
  }

  const { vseGcsUrl, vsePublicUrl, fileName } = readVseFileUrls(vse);
  if (!vseGcsUrl && !vsePublicUrl) {
    throw new Error("editor_import_vse_file_info_missing");
  }

  return {
    viewRData,
    targetSpaceIndex,
    vseIndex,
    vseGcsUrl,
    vsePublicUrl,
    fileName,
  };
}

/** Unity 리로드 이후 React store에 갱신된 ViewRData가 반영될 때까지 대기합니다. */
export function waitForViewRDataAfterEditorImportReload({
  targetSpaceIndex,
  expectedVseIndex,
  timeoutMs = DEFAULT_VIEWR_DATA_SYNC_TIMEOUT_MS,
}: {
  targetSpaceIndex: number;
  expectedVseIndex: number;
  timeoutMs?: number;
}): Promise<ViewRData> {
  return new Promise((resolve, reject) => {
    let settled = false;
    let unsubscribe: (() => void) | null = null;
    let timeoutId: number | null = null;

    const finish = (error: Error | null, data?: ViewRData) => {
      if (settled) return;
      settled = true;
      if (unsubscribe) unsubscribe();
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
      }
      if (error) reject(error);
      else if (data) resolve(data);
      else reject(new Error("editor_import_viewr_data_sync_timeout"));
    };

    const tryResolve = (candidate: ViewRData | null | undefined) => {
      try {
        const ready = validateEditorImportVseEntry(
          candidate,
          targetSpaceIndex,
          expectedVseIndex,
        );
        finish(null, ready.viewRData);
        return true;
      } catch {
        return false;
      }
    };

    if (tryResolve(useViewRDataStore.getState().viewRData)) {
      return;
    }

    unsubscribe = useViewRDataStore.subscribe((state) => {
      tryResolve(state.viewRData);
    });

    timeoutId = window.setTimeout(() => {
      finish(new Error("editor_import_viewr_data_sync_timeout"));
    }, timeoutMs);
  });
}

/**
 * create-vse hybrid 완료 후: GCS .viewr Unity 리로드 → ViewRData 동기화 → VSE 슬롯 검증.
 * 검증 통과 전에는 VSE 메시 로드를 보내지 않습니다.
 */
export async function prepareEditorObjectImportAfterConverter({
  uploadItemId,
  viewrGcsPath,
  targetSpaceIndex,
  jobId,
  item,
  hintViewRData,
  viewRDataSyncTimeoutMs,
}: {
  uploadItemId: string;
  viewrGcsPath: string;
  targetSpaceIndex: number;
  jobId?: string | null;
  item?: { editorImportVseIndex?: number | null } | null;
  hintViewRData?: ViewRData | null;
  viewRDataSyncTimeoutMs?: number;
}): Promise<PreparedEditorImportVseReadiness> {
  const expectedVseIndex = resolveEditorImportVseIndex({
    viewRData: hintViewRData ?? useViewRDataStore.getState().viewRData,
    targetSpaceIndex,
    item,
  });

  const transactionId = await reloadUpdatedViewrBeforeEditorImport({
    uploadItemId,
    viewrGcsPath,
    targetSpaceIndex,
    jobId,
    deferCurrentSpaceVseLoad: true,
  });

  try {
    await sendToUnityWithResponse("reactToUnity_requestViewRData", {
      reason: "editor_import_post_reload",
      targetSpaceIndex,
      expectedVseIndex,
    });
  } catch (error) {
    console.warn(
      "[EDITOR] requestViewRData after .viewr reload failed; waiting on store sync",
      error,
    );
  }

  const freshViewRData = await waitForViewRDataAfterEditorImportReload({
    targetSpaceIndex,
    expectedVseIndex,
    timeoutMs: viewRDataSyncTimeoutMs,
  });

  return {
    ...validateEditorImportVseEntry(
      freshViewRData,
      targetSpaceIndex,
      expectedVseIndex,
    ),
    transactionId,
  };
}

/** 메시 로드용 URL: index의 vseFileInfo 우선, 없으면 업로드 결과 fallback. */
export function resolveEditorImportMeshPayload(
  readiness: EditorImportVseReadiness,
  fallback?: EditorImportMeshFallback | null,
): {
  vseFileInfo: {
    fileName: string;
    vseGCSUrl: string;
    vsePublicUrl: string;
    description: string;
  };
  gcsPath: string;
} {
  const fallbackGcs = normalizeGsUrl(fallback?.vseGCSUrl);
  const fallbackPublic = normalizeGsUrl(fallback?.vsePublicUrl);
  const vseGCSUrl = readiness.vseGcsUrl || fallbackGcs;
  const vsePublicUrl = readiness.vsePublicUrl || fallbackPublic;
  const fileName =
    readiness.fileName ||
    (fallback?.fileName ?? "").trim() ||
    "asset.vse";

  return {
    vseFileInfo: {
      fileName,
      vseGCSUrl,
      vsePublicUrl,
      description: "",
    },
    gcsPath: vseGCSUrl || fallbackGcs,
  };
}
